import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ShadowRootWrapper } from '../ShadowRootWrapper';
import ArrowLeftIcon from './CustomIcons/ArrowLeftIcon';
import DismissIcon from './CustomIcons/DismissIcon';
import { OVERLAY_ICON_BUTTON_CLASSES, useOverlayBehaviour } from './overlayBehaviour';
import { OVERLAY_ELEVATED_ATTR, overlayIdentity } from './portalOverlay';

export interface SidePanelProps {
    open: boolean;
    /** Dismiss handler — close button, ESC, or overlay click. */
    onClose: () => void;
    title: React.ReactNode;
    /** Secondary text, inline after the title. */
    subtitle?: React.ReactNode;
    /** Content at the end of the title row, after the subtitle (e.g. a status tag). */
    titleExtra?: React.ReactNode;
    /** Block paragraph below the title row, spanning the header width. */
    description?: React.ReactNode;
    /** Divider under the header. Default: false. */
    headerDivider?: boolean;
    /** Leading header slot, typically a logo or icon. */
    icon?: React.ReactNode;
    /** Content pinned right in the header, before the close button. */
    headerExtra?: React.ReactNode;
    footer?: React.ReactNode;
    /** Renders a back button before the header icon. */
    onBack?: () => void;
    /** Maximum panel width. Default: 1080. */
    maxWidth?: number | string;
    /** Accessible dialog name; required when `title` isn't a plain string. */
    'aria-label'?: string;
    /** ID of a visible element naming the dialog (alternative to `aria-label`). */
    'aria-labelledby'?: string;
    /** Whether the body gets default padding. Default: true. */
    bodyPadding?: boolean;
    /** `above-app-overlays` lifts the overlay stack clear of chrome pinned near max-int. */
    stacking?: 'default' | 'above-app-overlays';
    children: React.ReactNode;
}

const TRANSITION_MS = 200;

const toCssSize = (value: number | string): string =>
    typeof value === 'number' ? `${value}px` : value;

/**
 * A right-hand slide-in overlay panel (drawer) for inspecting or editing an entity
 * without leaving the page. Portals to `document.body`, locks background scroll and
 * traps keyboard focus while open (restoring both on close), dismisses on ESC
 * (topmost open overlay only), overlay click or the close button, and exposes header
 * (back/icon/title/subtitle/extra), scrollable body and optional footer slots.
 */
const SidePanel: React.FC<SidePanelProps> = ({
    open,
    onClose,
    title,
    subtitle,
    titleExtra,
    description,
    headerDivider = false,
    icon,
    headerExtra,
    footer,
    onBack,
    maxWidth = 1080,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    bodyPadding = true,
    stacking = 'default',
    children,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const frameRef = useRef<number | null>(null);
    const unmountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hostRef = useRef<HTMLDivElement | null>(null);
    const {
        ref: handlePanelRef,
        isTopmost,
        zIndex,
        elevated,
    } = useOverlayBehaviour({
        open,
        onClose,
        elevated: stacking === 'above-app-overlays',
        hostRef,
    });

    useEffect(() => {
        if (open) {
            setIsMounted(true);
            if (unmountTimerRef.current) {
                clearTimeout(unmountTimerRef.current);
                unmountTimerRef.current = null;
            }
            frameRef.current = requestAnimationFrame(() => {
                frameRef.current = null;
                setIsVisible(true);
            });
        } else {
            setIsVisible(false);
            unmountTimerRef.current = setTimeout(() => {
                unmountTimerRef.current = null;
                setIsMounted(false);
            }, TRANSITION_MS);
        }
        return () => {
            if (frameRef.current !== null) {
                cancelAnimationFrame(frameRef.current);
                frameRef.current = null;
            }
            if (unmountTimerRef.current) {
                clearTimeout(unmountTimerRef.current);
                unmountTimerRef.current = null;
            }
        };
    }, [open]);

    if (!isMounted) {
        return null;
    }

    return createPortal(
        // Host carries the stacking context — ShadowRootWrapper's `isolation` would
        // otherwise flatten our z-index and let sticky page elements paint over us.
        <ShadowRootWrapper
            hostRef={hostRef}
            hostStyle={{ position: 'relative', zIndex }}
            hostProps={
                elevated
                    ? { ...overlayIdentity('sidepanel'), [OVERLAY_ELEVATED_ATTR]: '' }
                    : overlayIdentity('sidepanel')
            }
        >
            <div className="fixed inset-0 z-[1000]">
                <div
                    aria-hidden="true"
                    onClick={onClose}
                    className={`absolute inset-0 bg-[var(--malachite-overlay-scrim)] transition-opacity duration-200 ease-in-out ${
                        isVisible ? 'opacity-100' : 'opacity-0'
                    }`}
                />
                <div
                    ref={handlePanelRef}
                    role="dialog"
                    // Only the topmost overlay may claim it, or a screen reader can still
                    // browse the one underneath.
                    aria-modal={isTopmost ? 'true' : undefined}
                    tabIndex={-1}
                    // Prefer aria-labelledby; only fall back to aria-label when it's absent.
                    aria-label={
                        ariaLabelledBy
                            ? undefined
                            : (ariaLabel ?? (typeof title === 'string' ? title : undefined))
                    }
                    aria-labelledby={ariaLabelledBy}
                    className="absolute top-[8px] right-[8px] bottom-[8px] flex flex-col overflow-hidden rounded-[20px] border border-solid border-[var(--malachite-card-border)] bg-[var(--malachite-card-background)] text-[var(--malachite-card-foreground)] outline-none [box-shadow:var(--malachite-shadow-lg)]"
                    style={{
                        width: `min(calc(100% - 16px), ${toCssSize(maxWidth)})`,
                        // Inline: Tailwind translate utilities silently break inside
                        // shadow roots (see CLAUDE.md Shadow DOM caveats).
                        transform: isVisible ? 'translateX(0)' : 'translateX(calc(100% + 16px))',
                        transition: `transform ${TRANSITION_MS}ms ease-in-out`,
                    }}
                >
                    <div
                        className={`flex flex-col gap-[20px] px-[20px] py-[18px] ${
                            headerDivider
                                ? 'border-0 border-b border-solid border-[var(--malachite-card-border)]'
                                : ''
                        }`}
                    >
                        <div className="flex items-center gap-[12px]">
                            {onBack ? (
                                <button
                                    type="button"
                                    aria-label="Back"
                                    onClick={onBack}
                                    className={OVERLAY_ICON_BUTTON_CLASSES}
                                >
                                    <ArrowLeftIcon size={16} />
                                </button>
                            ) : null}
                            {icon ? (
                                <span className="flex shrink-0 items-center">{icon}</span>
                            ) : null}
                            <div className="flex min-w-0 flex-1 items-center gap-[8px]">
                                <span className="shrink-0 text-[length:var(--malachite-font-size-15,15px)] font-semibold text-[var(--malachite-text-primary)]">
                                    {title}
                                </span>
                                {subtitle ? (
                                    <span className="min-w-0 truncate text-[13px] text-[var(--malachite-text-secondary)]">
                                        {subtitle}
                                    </span>
                                ) : null}
                                {titleExtra ? (
                                    <span className="flex shrink-0 items-center">{titleExtra}</span>
                                ) : null}
                            </div>
                            {headerExtra ? (
                                <span className="flex shrink-0 items-center">{headerExtra}</span>
                            ) : null}
                            <button
                                type="button"
                                aria-label="Close"
                                onClick={onClose}
                                className={OVERLAY_ICON_BUTTON_CLASSES}
                            >
                                <DismissIcon size={10} />
                            </button>
                        </div>
                        {description ? (
                            <div className="text-[13px] leading-[1.6] text-[var(--malachite-text-secondary)]">
                                {description}
                            </div>
                        ) : null}
                    </div>
                    <div
                        className={`min-h-0 flex-1 overflow-y-auto ${bodyPadding ? 'px-[28px]' : ''}`}
                    >
                        {bodyPadding ? <div className="py-[28px]">{children}</div> : children}
                    </div>
                    {footer ? (
                        <div className="border-0 border-t border-solid border-[var(--malachite-card-border)] px-[24px] py-[16px]">
                            {footer}
                        </div>
                    ) : null}
                </div>
            </div>
        </ShadowRootWrapper>,
        document.body,
    );
};

export default SidePanel;
export { SidePanel };
